import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../../components/breadcrumbs'
import Footer from '../../../components/footer'
import { getCanonicalUrl } from '../../../utils/canonical'
import DarkNav from '../../../components/dark-nav'
import './zapier-feature.css'

const NewSMSReceived = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Zapier', path: '/integrations/zapier' },
    { label: 'New SMS Received' }
  ]

  const useCases = [
    {
      title: "Automated Customer Support Ticket Creation",
      industry: "Customer Support & Service",
      description: "Instantly convert incoming customer SMS enquiries into support tickets, ensuring no customer query goes unanswered. Perfect for Australian retail, hospitality, and service businesses managing high volumes of customer communication.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'Hi, I need help with my order #12345. It hasn't arrived yet.'",
        "DataFlows receives SMS instantly via webhook (no polling delay)",
        "Extract key information: customer phone number, message content, timestamp",
        "Create support ticket in Zendesk/Freshdesk with priority based on keywords",
        "Assign ticket to appropriate team (e.g., 'order' keywords route to fulfilment team)",
        "Send auto-reply SMS: 'Thanks for contacting us! Ticket #4567 created. We'll respond within 2 hours.'",
        "Log interaction in CRM (HubSpot/Salesforce) for customer history tracking"
      ],
      keywords: "SMS customer support Australia, automated ticketing system, customer service SMS, help desk automation Australia"
    },
    {
      title: "Appointment Confirmation and Reminder System",
      industry: "Healthcare & Professional Services",
      description: "Enable customers to confirm, reschedule, or cancel appointments via SMS, reducing no-shows and administrative workload for Australian medical practices, dental clinics, salons, and professional service providers.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Patient receives appointment reminder: 'Appointment 15-Nov 2PM. Reply YES to confirm, NO to cancel'",
        "Patient replies: 'YES'",
        "Zapier workflow triggered instantly when SMS received",
        "Parse message content: check for keywords (YES/NO/RESCHEDULE)",
        "If YES: Update appointment status to 'confirmed' in booking system (Acuity/Calendly/Square)",
        "If NO: Mark appointment as cancelled, send available time slots",
        "If RESCHEDULE: Send booking link via SMS for self-service rescheduling",
        "Log confirmation in Google Sheets/Airtable for reporting and analytics"
      ],
      keywords: "appointment confirmation SMS Australia, medical appointment reminders, salon booking system, reduce no-shows Australia"
    },
    {
      title: "Order Status and Tracking Request Automation",
      industry: "E-commerce & Logistics",
      description: "Allow Australian customers to check their order status by simply texting their order number, providing instant gratification and reducing support workload for online retailers and logistics companies.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS: 'ORDER 12345' or 'Where is my order?'",
        "Trigger fires immediately with message content and customer phone number",
        "Use formatter to extract order number from message (regex pattern matching)",
        "Look up order status in Shopify/WooCommerce/custom database via API",
        "Format response with order details: status, tracking number, estimated delivery",
        "Send SMS reply: 'Order #12345 dispatched 12-Nov. Australia Post tracking: AP123456789. Est. delivery: 15-Nov'",
        "If order not found: 'Order not found. Please check number or contact support at 1300-XXX-XXX'",
        "Track all order enquiries in Google Sheets for customer service analytics"
      ],
      keywords: "order tracking SMS Australia, e-commerce automation, customer self-service, delivery status updates Australia"
    },
    {
      title: "Two-Way SMS Conversation Management",
      industry: "Sales & Lead Generation",
      description: "Engage Australian prospects and customers in real-time two-way SMS conversations, automatically routing messages to the right sales rep or team member while maintaining conversation history and context.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Prospect sends SMS to your Australian business number: 'Interested in your solar panel quote'",
        "Workflow identifies if customer is existing (lookup in CRM) or new lead",
        "For existing customers: Route message to assigned account manager via Slack/email",
        "For new leads: Create new contact in CRM with source = 'SMS Inbound'",
        "Notify sales team in dedicated Slack channel with customer details and message",
        "Send auto-reply: 'Thanks for your interest! Our solar specialist will respond within 30 minutes.'",
        "Log entire conversation thread in CRM for context and follow-up",
        "Set reminder task for sales rep if no response sent within 30 minutes"
      ],
      keywords: "two-way SMS Australia, lead management SMS, sales automation Australia, SMS lead capture, real-time customer engagement"
    },
    {
      title: "SMS-to-Email Forwarding for Remote Teams",
      industry: "Field Service & Remote Teams",
      description: "Automatically forward all incoming SMS messages to email, perfect for Australian businesses with remote teams, field technicians, or employees who need SMS notifications in their email workflow.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Customer sends SMS to your business number: 'Urgent: Hot water system not working'",
        "Trigger captures all message details: from number, to number, message, timestamp",
        "Format email with professional template including customer phone number and message",
        "Send email to support@company.com.au with subject: 'New SMS from +61412345678'",
        "Include 'Reply via SMS' link that opens template for quick response",
        "Optionally route to specific team members based on message keywords (urgent/booking/quote)",
        "Store copy of message in Google Sheets for compliance and record-keeping",
        "Send SMS auto-reply: 'Message received. Our team will respond via email or call within 1 hour.'"
      ],
      keywords: "SMS to email forwarding Australia, remote team communication, field service management, SMS notification system"
    },
    {
      title: "Contact Form and Lead Capture via SMS",
      industry: "Real Estate & Property Management",
      description: "Enable Australian property seekers to request information by texting property codes, instantly sending them details and capturing leads for follow-up by real estate agents.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Prospect sees property sign: 'Text PROP123 to 0400 XXX XXX for details'",
        "Prospect sends SMS: 'PROP123'",
        "Workflow extracts property code from message using text formatter",
        "Look up property details in Airtable/database (address, price, bedrooms, photos link)",
        "Send SMS with property summary and virtual tour link",
        "Create new lead in real estate CRM with property interest tagged",
        "Notify assigned agent via email/Slack with prospect details and interested property",
        "Schedule follow-up task for agent to call prospect within 24 hours",
        "Add prospect to property-specific email drip campaign for open home invitations"
      ],
      keywords: "real estate SMS marketing Australia, property enquiry automation, SMS lead generation, real estate lead capture Australia"
    },
    {
      title: "Event RSVP and Registration Management",
      industry: "Events & Hospitality",
      description: "Manage event registrations and RSVPs via SMS for Australian events, festivals, and hospitality venues, making it easy for attendees to confirm attendance or request information.",
      trigger: "DataFlows SMS - New SMS Received",
      steps: [
        "Invited guest receives: 'Melbourne Wine Festival 20-Nov. Reply YES to attend, NO to decline, INFO for details'",
        "Guest replies: 'YES' or 'YES +1' (bringing guest)",
        "Workflow parses response and extracts RSVP status and guest count",
        "Update event registration in Eventbrite/Google Sheets with attendance confirmation",
        "Calculate and update total attendee count for venue capacity planning",
        "Send confirmation SMS with event details: 'See you there! Melbourne Wine Festival, 20-Nov 6PM, Federation Square. E-ticket: [link]'",
        "If capacity reached: Add to waitlist and notify 'Event full. You're #5 on waitlist'",
        "Send reminder SMS 24 hours before event with directions and parking info"
      ],
      keywords: "event RSVP SMS Australia, event management automation, festival registration, hospitality booking system Australia"
    }
  ]

  const faqItems = [
    {
      question: "How quickly does the New SMS Received trigger fire in Zapier?",
      answer: "The trigger fires instantly using webhooks (not polling). When someone sends an SMS to your DataFlows virtual number, the webhook delivers the message to Zapier in real-time, typically within 1-2 seconds. This is much faster than polling-based triggers which can have 5-15 minute delays depending on your Zapier plan."
    },
    {
      question: "What information is provided when an SMS is received?",
      answer: "The trigger provides all essential SMS data: message ID (unique identifier), from (sender's phone number in E.164 format like +61412345678), to (your virtual number), message (the SMS content), and received_at (timestamp in ISO format). This comprehensive data allows you to build sophisticated workflows with full message context."
    },
    {
      question: "Can I use this trigger with Australian phone numbers?",
      answer: "Yes! DataFlows specialises in Australian SMS services. The trigger works with all Australian mobile numbers (+614XX XXX XXX) and supports both local and international senders. All phone numbers are provided in standardised E.164 format (+61 country code) for easy parsing and database storage."
    },
    {
      question: "Does this trigger work for both standard SMS and long messages?",
      answer: "Absolutely. The trigger handles standard SMS (up to 160 characters), long concatenated messages (multiple parts automatically joined), and MMS messages. Australian customers can send messages of any length, and you'll receive the complete message content in the 'message' field, ready for processing."
    },
    {
      question: "Can I filter which SMS messages trigger the workflow?",
      answer: "Yes! Use Zapier's built-in filter step to process only specific messages. Common filters include: message content contains specific keywords (e.g., 'URGENT', 'ORDER', 'HELP'), sender phone number matches customer database, messages received during business hours only, or messages that match specific patterns using regex. This prevents unnecessary workflow runs and reduces costs."
    },
    {
      question: "How do I prevent my workflow from triggering for sent messages?",
      answer: "The New SMS Received trigger only fires for incoming messages to your virtual number, never for messages you send. DataFlows has a separate 'New SMS Sent' trigger for tracking outbound messages. This ensures your workflows only process customer-initiated communications without duplication."
    },
    {
      question: "What happens if I receive multiple SMS messages simultaneously?",
      answer: "Each SMS message creates a separate trigger event and runs your workflow independently. If you receive 10 SMS messages within a second, Zapier will run your workflow 10 times (once per message). This parallel processing ensures every customer message is handled promptly without queuing delays, perfect for high-volume Australian businesses."
    },
    {
      question: "Can I reply to received SMS messages automatically?",
      answer: "Yes! Use the DataFlows SMS 'Send SMS' action in your workflow to send automatic replies. You can extract the sender's phone number from the trigger data (the 'from' field) and use it as the recipient for your reply. This enables two-way conversations, auto-replies, confirmations, and interactive SMS workflows for Australian customers."
    },
    {
      question: "Is there a limit to how many SMS messages can trigger workflows?",
      answer: "The limit depends on your Zapier plan (number of tasks per month) and your DataFlows SMS credits (number of messages). DataFlows has no technical limit on incoming messages, so you can process high volumes. For Australian businesses with variable SMS volumes, we recommend monitoring your Zapier task usage and upgrading if needed."
    },
    {
      question: "How can I test the trigger before going live?",
      answer: "When setting up your Zap, Zapier will prompt you to test the trigger. Simply send a test SMS to your DataFlows virtual number from your mobile phone, then click 'Test trigger' in Zapier. You'll see the message data appear immediately, allowing you to verify all fields are captured correctly before activating your workflow for real Australian customer messages."
    }
  ]

  return (
    <div className="zapier-feature-container">
      <DarkNav />
      <Helmet>
        <title>New SMS Received Trigger - DataFlows Zapier | SMS Webhook Australia</title>
        <meta
          name="description"
          content="Automate workflows when SMS received on your Australian number. Real-time SMS webhooks, instant triggers, no polling delays. SMS gateway Australia, SMS API, receive SMS Australia. Perfect for customer support, appointments, orders."
        />
        <meta name="keywords" content="SMS gateway Australia, SMS API Australia, receive SMS Australia, SMS webhook, real-time SMS, SMS automation Australia, inbound SMS trigger, SMS integration Zapier, Australian SMS service, business SMS Australia" />
        <meta property="og:title" content="New SMS Received Trigger for Australian Businesses - DataFlows Zapier" />
        <meta property="og:description" content="Instantly trigger Zapier workflows when customers text your Australian business number. Automate support tickets, appointments, orders, and more with real-time SMS webhooks." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/zapier/triggers/new-sms-received')} />
      </Helmet>

      <div className="feature-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="feature-hero" data-aos="fade-up">
        <div className="container">
          <div className="hero-badge">Real-Time Webhook Trigger</div>
          <h1>New SMS Received</h1>
          <p className="hero-subtitle">
            Instantly trigger Zapier workflows when customers text your Australian business number. Automate customer support, appointment confirmations, order enquiries, and two-way conversations with real-time SMS webhooks—no polling delays.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-value">Instant</div>
              <div className="stat-label">Webhook Delivery</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">6</div>
              <div className="stat-label">Data Fields</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">24/7</div>
              <div className="stat-label">Monitoring</div>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-overview" data-aos="fade-up">
        <div className="container">
          <h2>What Is the New SMS Received Trigger?</h2>
          <p className="section-intro">
            The New SMS Received trigger is a powerful Zapier integration that automatically fires your workflows the moment an SMS message arrives at your DataFlows virtual Australian number. Unlike polling-based triggers that check for new messages every 5-15 minutes, our webhook-based trigger delivers messages to Zapier instantly—typically within 1-2 seconds of receipt. This real-time capability makes it perfect for time-sensitive Australian business communications like customer support, appointment confirmations, order status requests, and emergency notifications.
          </p>

          <div className="overview-grid">
            <div className="overview-card">
              <div className="card-icon">⚡</div>
              <h3>Instant Webhook Delivery</h3>
              <p>No polling delays. Messages arrive in Zapier within 1-2 seconds of being sent, enabling real-time customer service and immediate automated responses for Australian businesses.</p>
              <div className="emotion-tags">
                <span>Real-Time</span>
                <span>No Delays</span>
                <span>Always On</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">📊</div>
              <h3>Complete Message Data</h3>
              <p>Receive all message details including sender number, recipient number, message content, unique message ID, and precise timestamp—everything you need to build sophisticated SMS workflows.</p>
              <div className="emotion-tags neutral">
                <span>Message ID</span>
                <span>From/To Numbers</span>
                <span>Content</span>
                <span>Timestamp</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">🇦🇺</div>
              <h3>Australian Number Support</h3>
              <p>Designed for Australian businesses with local virtual numbers. Receive SMS from any Australian mobile or landline, with numbers formatted in standard E.164 international format (+614XX XXX XXX).</p>
              <div className="emotion-tags">
                <span>AU Numbers</span>
                <span>E.164 Format</span>
                <span>Local Service</span>
              </div>
            </div>
          </div>

          <div className="overview-grid" style={{ marginTop: '2rem' }}>
            <div className="overview-card">
              <div className="card-icon">🔄</div>
              <h3>Two-Way Communication</h3>
              <p>Combine with the Send SMS action to create interactive two-way conversations. Auto-reply to customers, send confirmations, request additional information, and maintain ongoing SMS dialogues.</p>
              <div className="emotion-tags">
                <span>Auto-Reply</span>
                <span>Interactive</span>
                <span>Conversational</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">🔗</div>
              <h3>6,000+ App Integrations</h3>
              <p>Connect incoming SMS to any of Zapier's 6,000+ apps. Create support tickets in Zendesk, log to Google Sheets, send Slack notifications, update CRM records, and countless other automations.</p>
              <div className="emotion-tags neutral">
                <span>Zendesk</span>
                <span>Slack</span>
                <span>Sheets</span>
                <span>HubSpot</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon">📱</div>
              <h3>Long Message Support</h3>
              <p>Handles standard SMS (160 chars), long concatenated messages (multiple parts), and even messages with emojis and special characters. Australian customers can send messages of any length.</p>
              <div className="emotion-tags">
                <span>Any Length</span>
                <span>Unicode</span>
                <span>Emojis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works" data-aos="fade-up">
        <div className="container">
          <h2>How the New SMS Received Trigger Works</h2>
          <p className="section-intro">
            DataFlows SMS uses instant webhook technology to deliver incoming messages to your Zapier workflows in real-time. Here's exactly what happens when a customer texts your Australian business number.
          </p>

          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Customer Sends SMS</h3>
                <p>A customer texts your DataFlows virtual Australian number from their mobile phone. The message is received by the Australian telecommunications network and routed to DataFlows.</p>
                <div className="code-example">
                  Customer's phone: +61412345678<br/>
                  Your business number: +61400123456<br/>
                  Message: "Hi, I need help with order #12345"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>DataFlows Receives SMS</h3>
                <p>DataFlows SMS gateway receives the message through our Australian carrier connections. The message is processed, validated, and prepared for delivery to your integrations.</p>
                <ul>
                  <li>Message is logged with unique ID for tracking and auditing</li>
                  <li>Phone numbers are standardised to E.164 format (+61...)</li>
                  <li>Timestamp is recorded in UTC for consistent timezone handling</li>
                  <li>Message content is preserved exactly as sent (including emojis)</li>
                </ul>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Webhook Fires to Zapier</h3>
                <p>DataFlows instantly sends a webhook to Zapier with complete message data. This happens in real-time (1-2 seconds) with no polling delays.</p>
                <div className="code-example">
                  id: "sms_abc123xyz789"<br/>
                  message_id: "msg_987654321"<br/>
                  from: "+61412345678"<br/>
                  to: "+61400123456"<br/>
                  message: "Hi, I need help with order #12345"<br/>
                  received_at: "2024-11-15T04:23:17Z"
                </div>
              </div>
            </div>

            <div className="workflow-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Your Workflow Runs</h3>
                <p>Zapier executes your configured workflow using the message data. You can filter, format, route, and process the message with unlimited possibilities.</p>
                <ul>
                  <li>Extract order number from message using text formatters</li>
                  <li>Look up customer in your CRM using the phone number</li>
                  <li>Create support ticket with message content and customer details</li>
                  <li>Send automated SMS reply confirming receipt</li>
                  <li>Notify your team via Slack or email</li>
                  <li>Log the interaction to Google Sheets or database</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="use-cases-section" data-aos="fade-up">
        <div className="container">
          <h2>Real-World Use Cases for Australian Businesses</h2>
          <p className="section-intro">
            Discover how Australian businesses across industries use the New SMS Received trigger to automate customer communications, improve response times, and deliver exceptional service through SMS automation.
          </p>

          {useCases.map((useCase, index) => (
            <div key={index} className="use-case-detailed" data-aos="fade-up">
              <div className="use-case-header">
                <div>
                  <h3>{useCase.title}</h3>
                  <span className="industry-tag">{useCase.industry}</span>
                </div>
              </div>
              <p className="use-case-description">{useCase.description}</p>

              <div className="workflow-breakdown">
                <h4>Workflow Steps:</h4>
                <ol>
                  {useCase.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>

            </div>
          ))}
        </div>
      </section>

      <section className="australian-focus" data-aos="fade-up">
        <div className="container">
          <h2>Why Australian Businesses Choose DataFlows SMS Gateway</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>🇦🇺 Australian-Based Infrastructure</h3>
              <p>Local SMS gateway with direct connections to Australian carriers (Telstra, Optus, Vodafone). Reliable delivery, low latency, and full support for Australian mobile numbers and regulations.</p>
            </div>
            <div className="benefit-item">
              <h3>⚡ Instant Webhook Technology</h3>
              <p>Real-time message delivery to Zapier with no polling delays. Messages arrive in 1-2 seconds, enabling time-sensitive workflows like urgent support requests and appointment confirmations.</p>
            </div>
            <div className="benefit-item">
              <h3>📞 Virtual Australian Numbers</h3>
              <p>Get dedicated virtual numbers in Sydney, Melbourne, Brisbane, Perth, Adelaide, and regional areas. Choose local area codes to build trust with Australian customers.</p>
            </div>
            <div className="benefit-item">
              <h3>🔒 ACMA Compliant & Secure</h3>
              <p>Fully compliant with Australian Communications and Media Authority (ACMA) regulations and Privacy Act 1988. Secure message handling with encryption and audit logs.</p>
            </div>
            <div className="benefit-item">
              <h3>💰 Transparent Australian Pricing</h3>
              <p>Simple per-message pricing in AUD with no hidden fees. Wholesale rates for high-volume Australian businesses. No setup fees or monthly minimums on pay-as-you-go plans.</p>
            </div>
            <div className="benefit-item">
              <h3>🔗 Deep Zapier Integration</h3>
              <p>Native Zapier app with multiple triggers and actions. Access 6,000+ app integrations to connect SMS with your existing business tools and workflows.</p>
            </div>
            <div className="benefit-item">
              <h3>📊 Message Analytics & Reporting</h3>
              <p>Track all incoming messages with detailed logs, delivery receipts, and response times. Export data for analysis, compliance, and business intelligence.</p>
            </div>
            <div className="benefit-item">
              <h3>🛠️ Developer-Friendly SMS API</h3>
              <p>RESTful SMS API for custom integrations beyond Zapier. Comprehensive documentation, SDKs, and webhooks for Australian developers building SMS features.</p>
            </div>
            <div className="benefit-item">
              <h3>⏰ 24/7 Message Processing</h3>
              <p>Receive and process SMS messages around the clock. Perfect for Australian businesses serving customers across multiple time zones or providing after-hours support.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="setup-guide" data-aos="fade-up">
        <div className="container">
          <h2>How to Set Up New SMS Received Trigger in Zapier</h2>
          <p className="section-intro">
            Follow this step-by-step guide to start receiving SMS messages in your Zapier workflows. Setup takes just 5 minutes and requires no technical expertise.
          </p>

          <div className="setup-step">
            <h3>Step 1: Get Your DataFlows SMS Account</h3>
            <ol>
              <li>Sign up for a free DataFlows SMS account at <a href="https://sms.dataflows.com.au" target="_blank" rel="noopener noreferrer">sms.dataflows.com.au</a></li>
              <li>Choose and purchase an Australian virtual number (Sydney, Melbourne, Brisbane, or other cities)</li>
              <li>Verify your account via email confirmation</li>
              <li>Navigate to Settings &gt; API to generate your API token</li>
              <li>Copy your API token—you'll need this to connect Zapier</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 2: Create Your Zap in Zapier</h3>
            <ol>
              <li>Log in to your <a href="https://zapier.com" target="_blank" rel="noopener noreferrer">Zapier account</a> (free plan works great for testing)</li>
              <li>Click the <strong>"Create Zap"</strong> button in the top navigation</li>
              <li>In the trigger section, search for <strong>"DataFlows SMS"</strong></li>
              <li>Select "DataFlows SMS" from the results</li>
              <li>You'll see a list of available triggers—choose <strong>"New SMS Received"</strong></li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 3: Connect Your DataFlows Account</h3>
            <ol>
              <li>Click <strong>"Sign in to DataFlows SMS"</strong> when prompted</li>
              <li>Paste your API token from Step 1 into the authentication field</li>
              <li>Click <strong>"Yes, Continue"</strong> to authorise the connection</li>
              <li>Zapier will verify your credentials and confirm successful connection</li>
              <li>Your DataFlows SMS account is now connected and ready to use</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 4: Configure Trigger Settings</h3>
            <ol>
              <li>Select which virtual number to monitor (if you have multiple Australian numbers)</li>
              <li>Optionally configure filters:
                <ul>
                  <li>Only messages from specific senders (whitelist phone numbers)</li>
                  <li>Only messages to specific virtual numbers</li>
                  <li>Messages received during specific hours (business hours only)</li>
                </ul>
              </li>
              <li>Click <strong>"Continue"</strong> to proceed to testing</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 5: Test the Trigger</h3>
            <ol>
              <li>Send a test SMS to your DataFlows virtual number from your mobile phone</li>
              <li>Example test message: "Testing my new Zapier automation!"</li>
              <li>In Zapier, click <strong>"Test trigger"</strong></li>
              <li>Zapier will fetch your most recent SMS (should show your test message)</li>
              <li>Review the data fields to confirm everything is captured correctly:
                <ul>
                  <li><strong>id:</strong> Unique identifier for this specific SMS</li>
                  <li><strong>message_id:</strong> Message ID from the carrier</li>
                  <li><strong>from:</strong> Sender's phone number (your mobile in E.164 format)</li>
                  <li><strong>to:</strong> Your virtual Australian number</li>
                  <li><strong>message:</strong> The SMS content you sent</li>
                  <li><strong>received_at:</strong> Timestamp when message was received</li>
                </ul>
              </li>
              <li>Click <strong>"Continue"</strong> if the test data looks correct</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 6: Add Actions to Your Workflow</h3>
            <ol>
              <li>Click the <strong>"+"</strong> button to add an action to your Zap</li>
              <li>Choose what should happen when an SMS is received. Popular options:
                <ul>
                  <li><strong>Gmail:</strong> Send email notification with SMS content</li>
                  <li><strong>Google Sheets:</strong> Log all incoming messages to spreadsheet</li>
                  <li><strong>Slack:</strong> Post message to team channel with SMS details</li>
                  <li><strong>Zendesk:</strong> Create support ticket from SMS enquiry</li>
                  <li><strong>DataFlows SMS - Send SMS:</strong> Send automated reply</li>
                  <li><strong>HubSpot/Salesforce:</strong> Create or update contact with SMS log</li>
                </ul>
              </li>
              <li>Map the trigger data to your action fields (drag and drop from the trigger step)</li>
              <li>Common mappings:
                <ul>
                  <li>Use "from" field as customer phone number</li>
                  <li>Use "message" field as ticket description or email body</li>
                  <li>Use "received_at" for timestamp tracking</li>
                </ul>
              </li>
              <li>Test your action to ensure it works correctly</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 7: Add Filters (Optional but Recommended)</h3>
            <ol>
              <li>Between the trigger and action, add a <strong>Filter</strong> step</li>
              <li>Use filters to process only relevant messages:
                <ul>
                  <li><strong>Keyword filter:</strong> Only continue if "message" contains "URGENT" or "HELP"</li>
                  <li><strong>Number filter:</strong> Only continue if "from" is in your customer database</li>
                  <li><strong>Time filter:</strong> Only continue if received during business hours (9am-5pm AEST)</li>
                  <li><strong>Length filter:</strong> Only continue if message is longer than 10 characters (ignore junk)</li>
                </ul>
              </li>
              <li>Filters prevent unnecessary actions and save on Zapier task usage</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 8: Enable Multi-Path Workflows (Advanced)</h3>
            <ol>
              <li>Use Zapier's <strong>Paths</strong> feature to route different message types to different actions</li>
              <li>Example paths for Australian e-commerce business:
                <ul>
                  <li><strong>Path A:</strong> If message contains "ORDER", look up order and send status</li>
                  <li><strong>Path B:</strong> If message contains "RETURN", create return ticket in help desk</li>
                  <li><strong>Path C:</strong> If message contains "TRACK", send tracking link</li>
                  <li><strong>Path D:</strong> Otherwise, forward to support team email</li>
                </ul>
              </li>
              <li>Each path can have multiple actions for complex workflows</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 9: Test End-to-End</h3>
            <ol>
              <li>Before turning on your Zap, do thorough testing</li>
              <li>Send multiple test SMS messages with different content and scenarios</li>
              <li>Verify each action executes correctly (check emails sent, sheets updated, tickets created, etc.)</li>
              <li>Test edge cases: very long messages, messages with emojis, messages with special characters</li>
              <li>Confirm timing: send SMS and verify how quickly your workflow completes</li>
              <li>Check error handling: what happens if an action fails? Consider adding error notification steps</li>
            </ol>
          </div>

          <div className="setup-step">
            <h3>Step 10: Turn On Your Zap and Monitor</h3>
            <ol>
              <li>Once testing is complete, toggle the Zap to <strong>"ON"</strong></li>
              <li>Give your Zap a descriptive name: "SMS to Support Ticket - Australian Customers"</li>
              <li>Monitor Zap history for the first few hours to ensure smooth operation</li>
              <li>Check Zapier's task history to see when messages arrive and how they're processed</li>
              <li>Set up Zapier error notifications (Settings &gt; Errors) to be alerted if workflows fail</li>
              <li>Periodically review logs and optimise your workflow based on actual usage patterns</li>
              <li>Consider creating a dashboard in Google Sheets to track SMS volumes, response times, and customer satisfaction</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="faq-section" data-aos="fade-up">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section" data-aos="fade-up">
        <div className="container">
          <h2>Ready to Automate Your SMS Workflows?</h2>
          <p>Start receiving SMS messages in Zapier instantly with DataFlows SMS gateway for Australian businesses.</p>
          <div className="cta-buttons">
            <a href="https://zapier.com/apps/dataflows-sms" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              Get Started with Zapier
            </a>
            <Link to="/integrations/zapier" className="btn btn-secondary">
              View All Zapier Features
            </Link>
          </div>
        </div>
      </section>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default NewSMSReceived
